/**
 * P1 DIAGNOSTICS: Outbound Events Viewer
 * 
 * Displays outbound integration events (Meta CAPI, Google Ads, Zapier, etc.)
 * with filtering, health status, and timeline tracing capabilities.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { 
  Send, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  SkipForward, 
  RefreshCw,
  Copy,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ListFilter,
  Activity,
  User,
  Calendar,
  ArrowUpRight,
  XCircle,
} from "lucide-react";

// =============================================================================
// CONSTANTS
// =============================================================================

const DESTINATION_LABELS = {
  meta: "Meta",
  google_ads: "Google Ads",
  zapier: "Zapier",
  hubspot: "HubSpot",
  salesforce: "Salesforce",
  slack: "Slack",
  webhook: "Webhook",  // MA-ADMIN-008: Default for dispatch events
  trustedform: "TrustedForm",  // MA-COMPLIANCE-VISIBILITY-002
};

const EVENT_TYPE_LABELS = {
  // Meta CAPI events
  meta_conversion_sent: "Conversion Sent",
  meta_conversion_failed: "Conversion Failed",
  meta_conversion_retry: "Conversion Retry",
  meta_conversion_skipped: "Conversion Skipped",
  // MA-ADMIN-008: Dispatch events
  outbound_dispatch_attempt: "Dispatch Attempt",
  outbound_dispatch_success: "Dispatch Success",
  outbound_dispatch_failed: "Dispatch Failed",
  outbound_dispatch_skipped: "Dispatch Skipped",
  // Test delivery events
  test_delivery_success: "Test Success",
  test_delivery_failed: "Test Failed",
  // MA-COMPLIANCE-VISIBILITY-002: TrustedForm claim events
  trustedform_claim_attempted: "Claim Attempted",
  trustedform_claim_success: "Claim Success",
  trustedform_claim_failed: "Claim Failed",
  trustedform_claim_skipped: "Claim Skipped",
};

// MA-COMPLIANCE-VISIBILITY-002: Event Category filter options
const EVENT_CATEGORY_OPTIONS = [
  { value: "all", label: "All Events" },
  { value: "lead_dispatch", label: "Lead Dispatch" },
  { value: "booking_dispatch", label: "Booking Dispatch" },
  { value: "trustedform_claim", label: "TrustedForm Claim" },
];

// MA-ADMIN-008: Trigger labels for dispatch source
const TRIGGER_LABELS = {
  intake_immediate: "Immediate (Intake)",
  verification_success: "Verification Success",
  manual_override: "Manual Override",
  test_delivery: "Test Delivery",
  retry: "Retry",
};

// MA-COMPLIANCE-VISIBILITY-002: Skip reason labels
const SKIP_REASON_LABELS = {
  mode_capture_only: "Mode: Capture Only",
  mode_off: "Mode: Off",
  no_tenant_api_key: "No API Key Configured",
};

const CONVERSION_TYPE_LABELS = {
  booked: "Booked",
  sold: "Sold",
  not_qualified: "Not Qualified",
};

const OUTCOME_BADGES = {
  success: { label: "Success", className: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
  failed: { label: "Failed", className: "bg-red-500/20 text-red-400 border-red-500/30" },
  retrying: { label: "Retrying", className: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  skipped: { label: "Skipped", className: "bg-slate-500/20 text-slate-400 border-slate-500/30" },
  pending: { label: "Pending", className: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
};

const HEALTH_BADGES = {
  healthy: { label: "Healthy", className: "bg-emerald-500/20 text-emerald-400" },
  degraded: { label: "Degraded", className: "bg-amber-500/20 text-amber-400" },
  failing: { label: "Failing", className: "bg-red-500/20 text-red-400" },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

const formatDateTime = (isoString) => {
  if (!isoString) return "—";
  const date = new Date(isoString);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const truncateId = (id, length = 8) => {
  if (!id) return "—";
  return id.length > length ? `${id.substring(0, length)}...` : id;
};

// =============================================================================
// COMPONENT
// =============================================================================

export default function OutboundEventsViewer({ tenantId, className = "" }) {
  const navigate = useNavigate();
  const { selectedTenantId } = useTenant();
  const effectiveTenantId = tenantId || selectedTenantId;

  // State
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [healthSummary, setHealthSummary] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Filters
  const [destinationFilter, setDestinationFilter] = useState("all");
  const [outcomeFilter, setOutcomeFilter] = useState("all");
  const [triggerFilter, setTriggerFilter] = useState("all");
  const [eventCategoryFilter, setEventCategoryFilter] = useState("all");  // MA-COMPLIANCE-VISIBILITY-002
  const [leadIdFilter, setLeadIdFilter] = useState("");
  const [traceIdFilter, setTraceIdFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Detail modal
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // =============================================================================
  // API CALLS
  // =============================================================================

  const buildParams = useCallback(() => {
    const params = { page, page_size: pageSize };
    if (effectiveTenantId) params.tenant_id = effectiveTenantId;
    if (destinationFilter && destinationFilter !== "all") params.destination = destinationFilter;
    if (outcomeFilter && outcomeFilter !== "all") params.outcome = outcomeFilter;
    if (triggerFilter && triggerFilter !== "all") params.trigger = triggerFilter;
    // MA-COMPLIANCE-VISIBILITY-002: Add event_category filter
    if (eventCategoryFilter && eventCategoryFilter !== "all") params.event_category = eventCategoryFilter;
    if (leadIdFilter.trim()) params.lead_id = leadIdFilter.trim();
    if (traceIdFilter.trim()) params.trace_id = traceIdFilter.trim();
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return params;
  }, [effectiveTenantId, page, pageSize, destinationFilter, outcomeFilter, triggerFilter, eventCategoryFilter, leadIdFilter, traceIdFilter, startDate, endDate]);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = buildParams();
      const response = await api.get("/admin/outbound-events", { params });
      setEvents(response.data.events || []);
      setTotal(response.data.total || 0);
      setHasMore(response.data.has_more || false);
    } catch (err) {
      console.error("[OutboundEventsViewer] Error fetching events:", err);
      setError(err.response?.data?.detail || "Failed to load outbound events");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const fetchHealthSummary = useCallback(async () => {
    try {
      const params = effectiveTenantId ? { tenant_id: effectiveTenantId } : {};
      const response = await api.get("/admin/outbound-events/health/summary", { params });
      setHealthSummary(response.data.summaries || []);
    } catch (err) {
      console.error("[OutboundEventsViewer] Error fetching health:", err);
    }
  }, [effectiveTenantId]);

  // =============================================================================
  // EFFECTS
  // =============================================================================

  useEffect(() => {
    fetchEvents();
    fetchHealthSummary();
  }, [fetchEvents, fetchHealthSummary]);

  // =============================================================================
  // HANDLERS
  // =============================================================================

  const handleCopyTraceId = (traceId) => {
    navigator.clipboard.writeText(traceId);
    toast({ title: "Trace ID copied", description: traceId });
  };

  const handleViewDetails = (event) => {
    setSelectedEvent(event);
    setShowDetailModal(true);
  };

  const handleApplyFilters = () => {
    setPage(1);
    fetchEvents();
  };

  const handleClearFilters = () => {
    setDestinationFilter("all");
    setOutcomeFilter("all");
    setTriggerFilter("all");
    setEventCategoryFilter("all");  // MA-COMPLIANCE-VISIBILITY-002
    setLeadIdFilter("");
    setTraceIdFilter("");
    setStartDate("");
    setEndDate("");
    setPage(1);
  };

  // =============================================================================
  // RENDER HELPERS
  // =============================================================================

  const renderOutcomeBadge = (outcome) => {
    const config = OUTCOME_BADGES[outcome] || OUTCOME_BADGES.skipped;
    return (
      <Badge className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const renderHealthBadge = (status) => {
    const config = HEALTH_BADGES[status] || HEALTH_BADGES.healthy;
    return (
      <Badge className={config.className}>
        {status === "healthy" && <CheckCircle className="w-3 h-3 mr-1" />}
        {status === "degraded" && <AlertCircle className="w-3 h-3 mr-1" />}
        {status === "failing" && <AlertCircle className="w-3 h-3 mr-1" />}
        {config.label}
      </Badge>
    );
  };

  const getEventLabel = (event) => {
    // MA-ADMIN-008: Handle dispatch events with trigger info
    if (event.trigger && TRIGGER_LABELS[event.trigger]) {
      const baseLabel = EVENT_TYPE_LABELS[event.event_type] || event.event_type;
      return `${baseLabel}`;
    }
    if (event.conversion_type) {
      return CONVERSION_TYPE_LABELS[event.conversion_type] || event.conversion_type;
    }
    return EVENT_TYPE_LABELS[event.event_type] || event.event_type;
  };

  const getDestinationLabel = (event) => {
    // MA-ADMIN-008: Use destination_name if available (from dispatch events)
    if (event.destination_name) {
      return event.destination_name;
    }
    const destLabel = DESTINATION_LABELS[event.destination] || event.destination || "Webhook";
    if (event.conversion_event_name) {
      return `${destLabel} ${event.conversion_event_name}`;
    }
    return destLabel;
  };

  // MA-ADMIN-008: Get trigger label for dispatch events
  const getTriggerLabel = (trigger) => {
    return TRIGGER_LABELS[trigger] || trigger;
  };

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <Card className={`bg-slate-900/50 border-slate-800 ${className}`}>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl text-slate-100 flex items-center gap-2">
              <Send className="w-5 h-5 text-indigo-400" />
              Outbound Events
            </CardTitle>
            <CardDescription className="text-slate-400 mt-1">
              View outbound integration events and API call results
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {/* Health Status Badges */}
            {healthSummary.map((summary) => (
              <div key={summary.destination} className="flex items-center gap-2">
                <span className="text-sm text-slate-400">
                  {DESTINATION_LABELS[summary.destination] || summary.destination}:
                </span>
                {renderHealthBadge(summary.status)}
              </div>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { fetchEvents(); fetchHealthSummary(); }}
              className="text-slate-400 hover:text-slate-200"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="border-slate-700 text-slate-300"
            >
              <ListFilter className="w-4 h-4 mr-1" />
              Filters
            </Button>
            {(destinationFilter !== "all" || outcomeFilter !== "all" || triggerFilter !== "all" || eventCategoryFilter !== "all" || leadIdFilter || traceIdFilter || startDate || endDate) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                className="text-slate-400"
              >
                Clear
              </Button>
            )}
            {/* MA-COMPLIANCE-VISIBILITY-002: Active Event Type Badge */}
            {eventCategoryFilter !== "all" && (
              <Badge
                variant="outline"
                className="bg-indigo-500/10 text-indigo-300 border-indigo-500/30 gap-1"
              >
                {EVENT_CATEGORY_OPTIONS.find(o => o.value === eventCategoryFilter)?.label || eventCategoryFilter}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEventCategoryFilter("all");
                    setPage(1);
                  }}
                  className="h-4 w-4 p-0 ml-1 text-indigo-400 hover:text-indigo-200"
                >
                  <XCircle className="w-3 h-3" />
                </Button>
              </Badge>
            )}
            {/* Active Trace ID Filter Badge */}
            {traceIdFilter && (
              <Badge
                variant="outline"
                className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30 gap-1"
              >
                <Activity className="w-3 h-3" />
                Trace: {truncateId(traceIdFilter, 12)}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setTraceIdFilter("");
                    setPage(1);
                  }}
                  className="h-4 w-4 p-0 ml-1 text-emerald-400 hover:text-emerald-200"
                  data-testid="outbound-events-trace-badge-clear"
                >
                  <XCircle className="w-3 h-3" />
                </Button>
              </Badge>
            )}
          </div>

          {showFilters && (
            <div className="flex flex-wrap items-end gap-3 p-3 bg-slate-800/50 rounded-lg">
              {/* MA-COMPLIANCE-VISIBILITY-002: Primary Event Type Filter */}
              <div className="space-y-1 w-40">
                <Label className="text-xs text-slate-400 font-semibold">Event Type</Label>
                <Select value={eventCategoryFilter} onValueChange={(val) => { setEventCategoryFilter(val); setPage(1); }}>
                  <SelectTrigger className="h-8 bg-slate-900 border-slate-700 text-slate-200" data-testid="event-category-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_CATEGORY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1 w-32">
                <Label className="text-xs text-slate-400">Destination</Label>
                <Select value={destinationFilter} onValueChange={setDestinationFilter}>
                  <SelectTrigger className="h-8 bg-slate-900 border-slate-700 text-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="webhook">Webhook</SelectItem>
                    <SelectItem value="trustedform">TrustedForm</SelectItem>
                    <SelectItem value="meta">Meta</SelectItem>
                    <SelectItem value="google_ads">Google Ads</SelectItem>
                    <SelectItem value="zapier">Zapier</SelectItem>
                    <SelectItem value="slack">Slack</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1 w-28">
                <Label className="text-xs text-slate-400">Outcome</Label>
                <Select value={outcomeFilter} onValueChange={setOutcomeFilter}>
                  <SelectTrigger className="h-8 bg-slate-900 border-slate-700 text-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="success">Success</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="retrying">Retrying</SelectItem>
                    <SelectItem value="skipped">Skipped</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1 w-36">
                <Label className="text-xs text-slate-400">Trigger</Label>
                <Select value={triggerFilter} onValueChange={setTriggerFilter}>
                  <SelectTrigger className="h-8 bg-slate-900 border-slate-700 text-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="intake_immediate">Immediate</SelectItem>
                    <SelectItem value="verification_success">Verified</SelectItem>
                    <SelectItem value="manual_override">Manual</SelectItem>
                    <SelectItem value="test_delivery">Test</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1 w-36">
                <Label className="text-xs text-slate-400">Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="h-8 bg-slate-900 border-slate-700 text-slate-200 [&::-webkit-calendar-picker-indicator]:opacity-50 [&::-webkit-calendar-picker-indicator]:hover:opacity-100"
                  data-testid="outbound-events-start-date"
                />
              </div>

              <div className="space-y-1 w-36">
                <Label className="text-xs text-slate-400">End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="h-8 bg-slate-900 border-slate-700 text-slate-200 [&::-webkit-calendar-picker-indicator]:opacity-50 [&::-webkit-calendar-picker-indicator]:hover:opacity-100"
                  data-testid="outbound-events-end-date"
                />
              </div>

              <div className="space-y-1 w-36">
                <Label className="text-xs text-slate-400">Lead ID</Label>
                <Input
                  value={leadIdFilter}
                  onChange={(e) => setLeadIdFilter(e.target.value)}
                  placeholder="Filter by lead..."
                  className="h-8 bg-slate-900 border-slate-700 text-slate-200 font-mono text-xs"
                />
              </div>

              <div className="space-y-1 w-40">
                <Label className="text-xs text-slate-400">Trace ID</Label>
                <div className="flex gap-1">
                  <Input
                    value={traceIdFilter}
                    onChange={(e) => setTraceIdFilter(e.target.value)}
                    placeholder="Filter by trace..."
                    className="h-8 bg-slate-900 border-slate-700 text-slate-200 font-mono text-xs"
                    data-testid="outbound-events-trace-id-filter"
                  />
                  {traceIdFilter && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setTraceIdFilter("");
                        setPage(1);
                      }}
                      className="h-8 px-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800"
                      data-testid="outbound-events-trace-id-clear"
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-6 px-4 bg-red-500/10 border border-red-500/20 rounded-lg">
            <AlertCircle className="w-6 h-6 mx-auto mb-2 text-red-400" />
            <p className="text-slate-300 font-medium">Unable to load outbound events</p>
            <p className="text-sm text-slate-400 mt-1">Please try again or contact support if the issue persists.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchEvents}
              className="mt-3 border-red-500/30 text-red-400 hover:bg-red-500/10"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Retry
            </Button>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Send className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No outbound events found</p>
            <p className="text-sm mt-1">Events will appear here when leads are dispatched or integrations are active</p>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead className="text-slate-400">Sent</TableHead>
                  <TableHead className="text-slate-400">Event</TableHead>
                  <TableHead className="text-slate-400">Outcome</TableHead>
                  <TableHead className="text-slate-400">Lead ID</TableHead>
                  <TableHead className="text-slate-400">Destination</TableHead>
                  <TableHead className="text-slate-400">Trigger</TableHead>
                  <TableHead className="text-slate-400">HTTP</TableHead>
                  <TableHead className="text-slate-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((event) => (
                  <TableRow key={event.id} className="border-white/5">
                    <TableCell className="text-slate-300 text-sm">
                      {formatDateTime(event.created_at)}
                    </TableCell>
                    <TableCell className="text-slate-200">
                      {getEventLabel(event)}
                    </TableCell>
                    <TableCell>
                      {renderOutcomeBadge(event.outcome)}
                    </TableCell>
                    <TableCell className="text-slate-400 font-mono text-xs">
                      {truncateId(event.lead_id, 12)}
                    </TableCell>
                    <TableCell className="text-slate-300">
                      {getDestinationLabel(event)}
                    </TableCell>
                    <TableCell>
                      {event.trigger ? (
                        <Badge 
                          className={
                            event.trigger === "verification_success" 
                              ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                              : event.trigger === "manual_override"
                              ? "bg-purple-500/20 text-purple-400 border-purple-500/30"
                              : event.trigger === "test_delivery"
                              ? "bg-blue-500/20 text-blue-400 border-blue-500/30"
                              : "bg-slate-500/20 text-slate-400 border-slate-500/30"
                          }
                        >
                          {getTriggerLabel(event.trigger)}
                        </Badge>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {event.response_status ? (
                        <Badge 
                          className={
                            event.response_status >= 200 && event.response_status < 300
                              ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                              : "bg-red-500/20 text-red-400 border-red-500/30"
                          }
                        >
                          {event.response_status}
                        </Badge>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 bg-indigo-500/10 border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/20"
                        onClick={() => handleViewDetails(event)}
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-800">
              <span className="text-sm text-slate-400">
                Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, total)} of {total}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  className="border-slate-700"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-400">Page {page}</span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasMore}
                  onClick={() => setPage(page + 1)}
                  className="border-slate-700"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Outbound Event Details</DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedEvent?.event_type} - {formatDateTime(selectedEvent?.created_at)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4 text-sm">
              {/* P1: Deep Link Buttons */}
              {(selectedEvent.lead_id || selectedEvent.appointment_id) && (
                <div className="flex flex-wrap gap-2 pb-3 border-b border-slate-700">
                  {selectedEvent.lead_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowDetailModal(false);
                        navigate(`/leads/${selectedEvent.lead_id}`);
                      }}
                      className="bg-indigo-500/10 border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/20 hover:text-indigo-200"
                      data-testid="outbound-event-open-lead"
                    >
                      <User className="h-4 w-4 mr-2" />
                      Open Lead
                      <ArrowUpRight className="h-3 w-3 ml-1.5" />
                    </Button>
                  )}
                  {selectedEvent.appointment_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowDetailModal(false);
                        navigate(`/appointments/${selectedEvent.appointment_id}`);
                      }}
                      className="bg-purple-500/10 border-purple-500/30 text-purple-300 hover:bg-purple-500/20 hover:text-purple-200"
                      data-testid="outbound-event-open-appointment"
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Open Appointment
                      <ArrowUpRight className="h-3 w-3 ml-1.5" />
                    </Button>
                  )}
                </div>
              )}

              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Outcome</Label>
                  <div className="mt-1">{renderOutcomeBadge(selectedEvent.outcome)}</div>
                </div>
                <div>
                  <Label className="text-slate-500">HTTP Status</Label>
                  <div className="mt-1 text-slate-200">{selectedEvent.response_status || "—"}</div>
                </div>
                <div>
                  <Label className="text-slate-500">Destination</Label>
                  <div className="mt-1 text-slate-200">{getDestinationLabel(selectedEvent)}</div>
                </div>
                <div>
                  <Label className="text-slate-500">Trigger</Label>
                  <div className="mt-1">
                    {selectedEvent.trigger ? (
                      <Badge 
                        className={
                          selectedEvent.trigger === "verification_success" 
                            ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                            : selectedEvent.trigger === "manual_override"
                            ? "bg-purple-500/20 text-purple-400 border-purple-500/30"
                            : selectedEvent.trigger === "test_delivery"
                            ? "bg-blue-500/20 text-blue-400 border-blue-500/30"
                            : "bg-slate-500/20 text-slate-400 border-slate-500/30"
                        }
                      >
                        {getTriggerLabel(selectedEvent.trigger)}
                      </Badge>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Response Time (for dispatch events) */}
              {selectedEvent.response_time_ms && (
                <div>
                  <Label className="text-slate-500">Response Time</Label>
                  <div className="mt-1 text-slate-200">{selectedEvent.response_time_ms}ms</div>
                </div>
              )}

              {/* Conversion Type (for Meta events) */}
              {selectedEvent.conversion_type && (
                <div>
                  <Label className="text-slate-500">Conversion Type</Label>
                  <div className="mt-1 text-slate-200">
                    {CONVERSION_TYPE_LABELS[selectedEvent.conversion_type] || selectedEvent.conversion_type}
                  </div>
                </div>
              )}

              {/* Trace ID */}
              {selectedEvent.trace_id && (
                <div>
                  <Label className="text-slate-500">Trace ID</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <code className="text-emerald-300 bg-emerald-500/10 px-2 py-1 rounded font-mono text-xs border border-emerald-500/20">
                      {selectedEvent.trace_id}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                      onClick={() => handleCopyTraceId(selectedEvent.trace_id)}
                      data-testid="outbound-event-copy-trace-id"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                      onClick={() => {
                        setTraceIdFilter(selectedEvent.trace_id);
                        setShowDetailModal(false);
                        setPage(1);
                        toast({
                          title: "Filtering by trace ID",
                          description: "Showing all outbound events in this journey",
                        });
                      }}
                      data-testid="outbound-event-filter-trace-id"
                    >
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Filter
                    </Button>
                  </div>
                </div>
              )}

              {/* Entity References */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Lead ID</Label>
                  <div className="mt-1 text-slate-200 font-mono text-xs">
                    {selectedEvent.lead_id || "—"}
                  </div>
                </div>
                <div>
                  <Label className="text-slate-500">Appointment ID</Label>
                  <div className="mt-1 text-slate-200 font-mono text-xs">
                    {selectedEvent.appointment_id || "—"}
                  </div>
                </div>
              </div>
              
              {/* MA-COMP-003: Consent Version in Payload (renamed to consent_version_id) */}
              {(selectedEvent.payload?.consent_version_id || selectedEvent.payload?.ma_consent_version || selectedEvent.consent_version_id || selectedEvent.ma_consent_version) && (
                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  <Label className="text-xs text-emerald-400">Consent Compliance (Payload)</Label>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-slate-400 text-xs">Version ID:</span>
                    <code className="text-xs bg-slate-800 px-1.5 py-0.5 rounded text-emerald-300">
                      {selectedEvent.payload?.consent_version_id || selectedEvent.payload?.ma_consent_version || selectedEvent.consent_version_id || selectedEvent.ma_consent_version}
                    </code>
                  </div>
                </div>
              )}

              {/* Retry Info */}
              {selectedEvent.attempt_number > 1 && (
                <div>
                  <Label className="text-slate-500">Retry Attempt</Label>
                  <div className="mt-1 text-slate-200">
                    {selectedEvent.attempt_number} / {selectedEvent.max_retries}
                  </div>
                </div>
              )}

              {/* Error Info */}
              {selectedEvent.error_message && (
                <div>
                  <Label className="text-slate-500">Error</Label>
                  <div className="mt-1 text-red-400 bg-red-500/10 p-2 rounded">
                    {selectedEvent.error_code && (
                      <span className="font-mono mr-2">[{selectedEvent.error_code}]</span>
                    )}
                    {selectedEvent.error_message}
                  </div>
                </div>
              )}

              {/* Request Payload */}
              {selectedEvent.request_payload && (
                <div>
                  <Label className="text-slate-500">Request Payload</Label>
                  <pre className="mt-1 text-slate-300 bg-slate-800 p-3 rounded text-xs overflow-x-auto">
                    {JSON.stringify(selectedEvent.request_payload, null, 2)}
                  </pre>
                </div>
              )}

              {/* Response Body */}
              {selectedEvent.response_body && (
                <div>
                  <Label className="text-slate-500">Response Body</Label>
                  <pre className="mt-1 text-slate-300 bg-slate-800 p-3 rounded text-xs overflow-x-auto">
                    {JSON.stringify(selectedEvent.response_body, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
